<template>
    <Section class="user-settings">

        <SectionHeader
            :title="$t('user.settings.title')"
            :subTitle="$t('user.settings.subTitle')"
            :image="require('@/assets/v3/img/features.png')"
        />

        <Card>
            <Loading v-if="isLoading"/>

            <el-form :model="formData" :rules="formRules" ref="form" label-position="top">
                <el-row>
                    <el-col :span="12" class="left">
                        <el-form-item :label="$t('user.settings.firstName')" prop="firstName">
                            <el-input v-model="formData.firstName"></el-input>
                        </el-form-item>
                    </el-col>
                    <el-col :span="12" class="right">
                        <el-form-item :label="$t('user.settings.lastName')" prop="lastName">
                            <el-input v-model="formData.lastName"></el-input>
                        </el-form-item>
                    </el-col>
                </el-row>
                <el-row>
                    <el-col :span="24">
                        <el-form-item :label="$t('user.settings.shortDescription')" prop="shortDescription">
                            <el-input v-model="formData.shortDescription"></el-input>
                        </el-form-item>
                    </el-col>
                </el-row>
                <el-row>
                    <el-col :span="24">
                        <el-form-item :label="$t('user.settings.linkedinProfileUrl')" prop="linkedinProfileUrl">
                            <el-input v-model="formData.linkedinProfileUrl" class="linkedin-input">
                                <template slot="prepend"><img :src="require('@/assets/v3/img/icons/linkedin.svg')">www.linkedin.com/in/</template>
                            </el-input>
                        </el-form-item>
                    </el-col>
                </el-row>
            </el-form>

            <footer>
                <PrimaryButton :text="$t('commons.reset')" class="big push-right" @click="reset"></PrimaryButton>
                <PrimaryButton :text="$t('commons.save')" class="big" @click="save"></PrimaryButton>
            </footer>
        </Card>
        <Card>
            <Loading v-if="isPictureLoading"/>
            <h2>{{$t('user.settings.userPicture.title')}}</h2>

            <ImageLoader
                :src="userPictureUrl"
                :default="require('@/assets/v3/img/icons/user-dark.png')"
                :preventCache="true" />

            <footer>
                <UploadButton
                    :text="$t('user.settings.userPicture.action')"
                    icon="s-icon-document_download"
                    accept="image/png, image/jpeg, image/jpg"
                    @complete="onUserPictureDataUploaded"
                    class="big icon-big push-right" />
            </footer>
        </Card>
        <Card>
            <Loading v-if="isLoadingEmail"/>
            <el-form :model="formEmailData" :rules="formEmailRules" ref="formEmail"
                label-position="top">
                <h2>{{$t('user.settings.changeEmail')}}</h2>
                <el-row>
                    <el-col :span="12" class="left">
                        <el-form-item :label="$t('user.settings.email')" prop="email">
                            <el-input v-model="formEmailData.email"></el-input>
                        </el-form-item>
                    </el-col>
                    <el-col :span="12" class="right">
                        <el-form-item :label="$t('user.settings.emailConfirm')" prop="emailConfirm">
                            <el-input v-model="formEmailData.emailConfirm"></el-input>
                        </el-form-item>
                    </el-col>
                </el-row>
            </el-form>

            <footer>
                <PrimaryButton :text="$t('user.settings.buttonChangeEmail')" class="big push-right" @click="changeUserEmail"></PrimaryButton>
            </footer>
        </Card>
        <Card>
            <Loading v-if="isLoadingPassword"/>
            <el-form :model="formPasswordData" :rules="formPasswordRules" ref="formPassword"
                label-position="top">
                <h2>{{$t('user.settings.changePassword')}}</h2>
                <el-row>
                    <el-col :span="12" class="left">
                        <el-form-item :label="$t('user.settings.password')" prop="password">
                            <el-input type="password" v-model="formPasswordData.password"></el-input>
                        </el-form-item>
                        <el-form-item :label="$t('user.settings.passwordConfirm')" prop="passwordConfirm">
                            <el-input type="password" v-model="formPasswordData.passwordConfirm"></el-input>
                        </el-form-item>
                    </el-col>
                    <el-col :span="12" class="right">
                        <p>{{$t('user.settings.changePasswordDesc')}}</p>
                    </el-col>
                </el-row>
            </el-form>

            <footer>
                <PrimaryButton :text="$t('user.settings.buttonChangePassword')" class="big push-right" @click="changeUserPassword"></PrimaryButton>
            </footer>
        </Card>
        <Card v-if="canDelete">
            <Loading v-if="isLoadingDelete"/>
            <h2>{{$t('user.settings.delete.title')}}</h2>
            <p>{{$t('user.settings.delete.description')}}</p>
            <footer>
                <PrimaryButton :text="$t('user.settings.delete.action')" class="big red push-right" @click="promptDeleteUser"></PrimaryButton>
            </footer>
        </Card>
        <Card v-if="canUnDelete">
            <Loading v-if="isLoadingDelete"/>
            <h2>{{$t('user.settings.undelete.title')}}</h2>
            <p>
                <span>{{$t('user.settings.undelete.description')}}</span>
                <span>{{$t('user.settings.undelete.closingTime', { timeStart: accountClosingTimeStart, timeEnd: accountClosingTimeEnd})}}</span>
            </p>
            <footer>
                <PrimaryButton :text="$t('user.settings.undelete.action')" class="big green push-right" @click="undeleteUser"></PrimaryButton>
            </footer>
        </Card>
    </Section>
</template>

<i18n>
{
    "en": {
        "user": {
            "settings": {
                "changeEmail": "Change email",
                "changeEmailSuccess": "Your email has been updated successfully",
                "buttonChangeEmail": "Update email",
                "buttonChangePassword": "Update password",
                "changePassword": "Change password",
                "changePasswordDesc": "Make sure it contains between 8 and 16 characters, a mixture of both uppercase and lowercase letters, a mixture of letters and numbers and at least one special character.",
                "changePasswordFailure": "An error has ocurred while trying to change your password. Try again later.",
                "changePasswordSuccess": "Your password has been updated successfully",
                "dataUpdateFailure": "An error occurred. Please try again later.",
                "dataUpdateSuccess": "Your data has been updated successfully",
                "email": "Email",
                "emailConfirm": "Confirm Email",
                "firstName": "First name",
                "inputRuleEmail": "Please enter your email address",
                "inputRuleEmailConfirm": "Please confirm your email address",
                "inputRuleFirstName": "Please enter your first name",
                "inputRuleLastName": "Please enter your last name",
                "inputRulePassword": "Please enter a valid password",
                "inputRulePasswordConfirm": "Please confirm the password",
                "inputRulePasswordLength": "Please enter between 6 and 16 characters",
                "inputRuleValidEmail": "Please enter a valid email address",
                "lastName": "Last name",
                "password": "Password",
                "passwordConfirm": "Confirm password",
                "title": "User Settings",
                "subTitle": "Change your personal data, email and password.",
                "shortDescription": "Short description of yourself",
                "linkedinProfileUrl": "Your LinkedIn profile url",
                "userPicture": {
                    "title": "Update your profile picture",
                    "action": "Upload a new picture",
                    "updateSuccess": "Your profile picture has been updated successfully"
                },
                "delete": {
                    "title": "Delete your account",
                    "description": "Start the process of permanently deleting your account. You will have 5 days to restore it before it is removed for good. Only do this if you are certain you no longer need your account.",
                    "action": "Delete account",
                    "confirm": {
                        "title": "Confirm account deletion",
                        "message": "Are you sure you want to delete your account? You will have 5 days to restore it. After this delay, your account and all of its data will be permanently deleted."
                    },
                    "success": {
                        "title": "Account waiting for deletion",
                        "message": "Your account will be deleted. You have 5 days to restore it. After this delay, your account and all of its data will be permanently deleted."
                    },
                    "error": {
                        "title": "Error deleting account",
                        "message": "An error has ocurred while trying to delete your account. Please try again later."
                    }
                },
                "undelete": {
                    "title": "Restore deleted account",
                    "description": "Cancel the deletion process and restore your account. You have 5 days since deletion to restore it.",
                    "closingTime": "The deletion process started at {timeStart} and will end around {timeEnd}",
                    "action": "Restore account",
                    "success": {
                        "title": "Account restored",
                        "message": "Your account has been restored successfully."
                    },
                    "error": {
                        "title": "Error restoring account",
                        "message": "An error has ocurred while trying to restore your account. Please try again later."
                    }
                }
            }
        }
    },
    "es": {
        "user": {
            "settings": {
                "changeEmail": "Cambiar correo electrónico",
                "changeEmailSuccess": "Su correo electrónico se ha actualizado correctamente",
                "buttonChangeEmail": "Actualizar correo electrónico",
                "buttonChangePassword": "Actualizar contraseña",
                "changePassword": "Cambiar contraseña",
                "changePasswordDesc": "Asegúrese de que contiene entre 8 y 16 caracteres, una mezcla de mayúsculas y minúsculas, una mezcla de letras y números y al menos un carácter especial.",
                "changePasswordFailure": "Se ha producido un error al intentar cambiar su contraseña. Vuelva a intentarlo más tarde.",
                "changePasswordSuccess": "Su contraseña se ha actualizado correctamente",
                "dataUpdateFailure": "Se ha producido un error. Vuelva a intentarlo más tarde.",
                "dataUpdateSuccess": "Sus datos se han actualizado correctamente",
                "email": "Envíe un correo electrónico ",
                "emailConfirm": "Confirmar correo electrónico",
                "firstName": "Nombre",
                "inputRuleEmail": "Introduzca su dirección de correo electrónico",
                "inputRuleEmailConfirm": "Confirme su dirección de correo electrónico",
                "inputRuleFirstName": "Por favor, introduzca su nombre",
                "inputRuleLastName": "Introduzca su apellido",
                "inputRulePassword": "Por favor, introduzca una contraseña válida",
                "inputRulePasswordConfirm": "Por favor, confirme la contraseña",
                "inputRulePasswordLength": "Introduzca entre 6 y 16 caracteres",
                "inputRuleValidEmail": "Por favor, introduzca una dirección de correo electrónico válida",
                "lastName": "Apellido",
                "password": "Contraseña",
                "passwordConfirm": "Confirmar contraseña",
                "title": "Configuración del usuario",
                "subTitle": "Cambia tus datos personales, correo electrónico y contraseña.",
                "shortDescription": "Breve descripción de sí mismo",
                "linkedinProfileUrl": "URL de su perfil de LinkedIn",
                "userPicture": {
                    "title": "Actualice su foto de perfil",
                    "action": "Cargar una nueva imagen",
                    "updateSuccess": "Tu foto de perfil se ha actualizado correctamente"
                },
                "delete": {
                    "title": "Eliminar su cuenta",
                    "description": "Inicia el proceso de eliminación definitiva de tu cuenta. Tendrás 5 días para restaurarla antes de que se elimine definitivamente. Hazlo solo si estás seguro de que ya no necesitas tu cuenta.",
                    "action": "Eliminar cuenta",
                    "confirm": {
                        "title": "Confirmar la eliminación de la cuenta",
                        "message": "¿Estás seguro de que quieres eliminar tu cuenta? Dispondrá de 5 días para restaurarla. Transcurrido este plazo, tu cuenta y todos sus datos se eliminarán definitivamente."
                    },
                    "success": {
                        "title": "Cuenta en espera de eliminación",
                        "message": "Tu cuenta será eliminada. Tienes 5 días para restaurarla. Transcurrido este plazo, tu cuenta y todos sus datos se eliminarán definitivamente."
                    },
                    "error": {
                        "title": "Error al eliminar la cuenta",
                        "message": "Se ha producido un error al intentar eliminar su cuenta. Vuelva a intentarlo más tarde."
                    }
                },
                "undelete": {
                    "title": "Restaurar cuenta eliminada",
                    "description": "Cancele el proceso de eliminación y restaure su cuenta. Tienes 5 días desde la eliminación para restaurarla.",
                    "closingTime": "El proceso de borrado comenzó en {timeStart} y finalizará alrededor de {timeEnd}.",
                    "action": "Restaurar cuenta",
                    "success": {
                        "title": "Cuenta restablecida",
                        "message": "Su cuenta ha sido restaurada con éxito."
                    },
                    "error": {
                        "title": "Error al restaurar la cuenta",
                        "message": "Se ha producido un error al intentar restaurar su cuenta. Vuelva a intentarlo más tarde."
                    }
                }
            }
        }
    },
    "fr": {
        "user": {
            "settings": {
                "changeEmail": "Modifier l'adresse e-mail",
                "changeEmailSuccess": "Votre adresse e-mail a été mise à jour avec succès",
                "buttonChangeEmail": "Mettre à jour l'adresse e-mail",
                "buttonChangePassword": "Modifier le mot de passe",
                "changePassword": "Modifier le mot de passe",
                "changePasswordDesc": "Assurez-vous qu'il contient entre 8 et 16 caractères, un mélange de lettres majuscules et minuscules, un mélange de lettres et de chiffres et au moins un caractère spécial.",
                "changePasswordFailure": "Une erreur s'est produite lors de la tentative de modification de votre mot de passe. Veuillez réessayer plus tard.",
                "changePasswordSuccess": "Votre mot de passe a été mis à jour avec succès",
                "dataUpdateFailure": "Une erreur s'est produite. Veuillez réessayer plus tard.",
                "dataUpdateSuccess": "Vos données ont été mises à jour avec succès",
                "email": "Courriel",
                "emailConfirm": "Confirmer l'e-mail",
                "firstName": "Prénom",
                "inputRuleEmail": "Veuillez saisir votre adresse e-mail",
                "inputRuleEmailConfirm": "Veuillez confirmer votre adresse e-mail",
                "inputRuleFirstName": "Veuillez saisir votre prénom",
                "inputRuleLastName": "Veuillez saisir votre nom de famille",
                "inputRulePassword": "Veuillez saisir un mot de passe valide",
                "inputRulePasswordConfirm": "Veuillez confirmer le mot de passe",
                "inputRulePasswordLength": "Veuillez saisir entre 6 et 16 caractères",
                "inputRuleValidEmail": "Veuillez saisir une adresse e-mail valide",
                "lastName": "Nom",
                "password": "Mot de passe",
                "passwordConfirm": "Confirmer le mot de passe",
                "title": "Paramètres utilisateur",
                "subTitle": "Modifiez vos données personnelles, votre adresse e-mail et votre mot de passe.",
                "shortDescription": "Courte description de vous-même",
                "linkedinProfileUrl": "L'URL de votre profil LinkedIn",
                "userPicture": {
                    "title": "Mettez à jour votre photo de profil",
                    "action": "Télécharger une nouvelle image",
                    "updateSuccess": "Votre photo de profil a été mise à jour avec succès"
                },
                "delete": {
                    "title": "Supprimer votre compte",
                    "description": "Démarrez le processus de suppression définitive de votre compte. Vous aurez 5 jours pour le restaurer avant qu'il ne soit supprimé définitivement. Ne le faites que si vous êtes certain de ne plus avoir besoin de votre compte.",
                    "action": "Supprimer le compte",
                    "confirm": {
                        "title": "Confirmer la suppression du compte",
                        "message": "Êtes-vous sûr de vouloir supprimer votre compte ? Vous aurez 5 jours pour le restaurer. Passé ce délai, votre compte et toutes ses données seront définitivement supprimés."
                    },
                    "success": {
                        "title": "Compte en attente de suppression",
                        "message": "Votre compte va être supprimé. Vous avez 5 jours pour le restaurer. Passé ce délai, votre compte et toutes ses données seront définitivement supprimés."
                    },
                    "error": {
                        "title": "Erreur lors de la suppression du compte",
                        "message": "Une erreur s'est produite lors de la suppression de votre compte. Veuillez réessayer plus tard."
                    }
                },
                "undelete": {
                    "title": "Restaurer le compte supprimé",
                    "description": "Annulez le processus de suppression et restaurez votre compte. Vous avez 5 jours à compter de la suppression pour le restaurer.",
                    "closingTime": "Le processus de suppression a commencé à {timeStart} et se terminera vers {timeEnd}",
                    "action": "Restaurer le compte",
                    "success": {
                        "title": "Compte restauré",
                        "message": "Votre compte a été restauré avec succès."
                    },
                    "error": {
                        "title": "Erreur lors de la restauration du compte",
                        "message": "Une erreur s'est produite lors de la tentative de restauration de votre compte. Veuillez réessayer plus tard."
                    }
                }
            }
        }
    },
    "pt": {
        "user": {
            "settings": {
                "changeEmail": "Alterar o correio eletrónico",
                "changeEmailSuccess": "O seu e-mail foi atualizado com sucesso",
                "buttonChangeEmail": "Atualizar o correio eletrónico",
                "buttonChangePassword": "Atualizar a palavra-passe",
                "changePassword": "Alterar palavra-passe",
                "changePasswordDesc": "Certifique-se de que contém entre 8 e 16 caracteres, uma mistura de letras maiúsculas e minúsculas, uma mistura de letras e números e, pelo menos, um carácter especial.",
                "changePasswordFailure": "Ocorreu um erro ao tentar alterar a sua palavra-passe. Tente novamente mais tarde.",
                "changePasswordSuccess": "A sua palavra-passe foi actualizada com sucesso",
                "dataUpdateFailure": "Ocorreu um erro. Por favor, tente novamente mais tarde.",
                "dataUpdateSuccess": "Os seus dados foram actualizados com sucesso",
                "email": "Email",
                "emailConfirm": "Confirmar Email",
                "firstName": "Primeiro nome",
                "inputRuleEmail": "Por favor introduza o seu endereço de correio electrónico",
                "inputRuleEmailConfirm": "Por favor, confirme o seu endereço de correio electrónico",
                "inputRuleFirstName": "Introduza o seu primeiro nome",
                "inputRuleLastName": "Introduza o seu apelido",
                "inputRulePassword": "Por favor introduza uma palavra-passe válida",
                "inputRulePasswordConfirm": "Favor confirmar a senha",
                "inputRulePasswordLength": "Por favor introduza entre 6 e 16 caracteres",
                "inputRuleValidEmail": "Por favor introduza um endereço de correio electrónico válido",
                "lastName": "Apelido",
                "password": "Senha",
                "passwordConfirm": "Confirmar palavra-passe",
                "title": "Configurações do utilizador",
                "subTitle": "Altere os seus dados pessoais, o seu correio eletrónico e a sua palavra-passe.",
                "shortDescription": "Breve descrição de si próprio",
                "linkedinProfileUrl": "URL do seu perfil no LinkedIn",
                "userPicture": {
                    "title": "Atualizar a sua imagem de perfil",
                    "action": "Carregar uma nova imagem",
                    "updateSuccess": "A sua fotografia de perfil foi actualizada com sucesso"
                },
                "delete": {
                    "title": "Eliminar a sua conta",
                    "description": "Iniciar o processo de eliminação permanente da sua conta. Terá 5 dias para a restaurar antes de a remover definitivamente. Faça isto apenas se tiver a certeza de que já não precisa da sua conta.",
                    "action": "Eliminar a conta",
                    "confirm": {
                        "title": "Confirmar a eliminação da conta",
                        "message": "Tem a certeza de que pretende apagar a sua conta? Dispõe de 5 dias para a restaurar. Após este prazo, a sua conta e todos os seus dados serão permanentemente eliminados."
                    },
                    "success": {
                        "title": "Conta à espera de ser eliminada",
                        "message": "A sua conta será eliminada. Dispõe de 5 dias para a restaurar. Após este prazo, a sua conta e todos os seus dados serão permanentemente apagados."
                    },
                    "error": {
                        "title": "Erro ao eliminar a conta",
                        "message": "Ocorreu um erro ao tentar eliminar a sua conta. Por favor, tente novamente mais tarde."
                    }
                },
                "undelete": {
                    "title": "Restaurar a conta eliminada",
                    "description": "Cancelar o processo de eliminação e restaurar a sua conta. Dispõe de 5 dias desde a eliminação para a restaurar.",
                    "closingTime": "O processo de eliminação começou em {timeStart} e terminará por volta de {timeEnd}",
                    "action": "Restaurar a conta",
                    "success": {
                        "title": "Conta restaurada",
                        "message": "A sua conta foi restaurada com êxito."
                    },
                    "error": {
                        "title": "Erro ao restaurar a conta",
                        "message": "Ocorreu um erro ao tentar restaurar a sua conta. Por favor, tente novamente mais tarde."
                    }
                }
            }
        }
    },
    "de": {
        "user": {
            "settings": {
                "changeEmail": "E-Mail ändern",
                "changeEmailSuccess": "Ihre E-Mail wurde erfolgreich aktualisiert",
                "buttonChangeEmail": "E-Mail aktualisieren",
                "buttonChangePassword": "Passwort aktualisieren",
                "changePassword": "Passwort ändern",
                "changePasswordDesc": "Achten Sie darauf, dass er zwischen 8 und 16 Zeichen enthält, eine Mischung aus Groß- und Kleinbuchstaben, eine Mischung aus Buchstaben und Zahlen und mindestens ein Sonderzeichen.",
                "changePasswordFailure": "Beim Versuch, Ihr Passwort zu ändern, ist ein Fehler aufgetreten. Versuchen Sie es später noch einmal.",
                "changePasswordSuccess": "Ihr Passwort wurde erfolgreich aktualisiert",
                "dataUpdateFailure": "Es ist ein Fehler aufgetreten. Bitte versuchen Sie es später noch einmal.",
                "dataUpdateSuccess": "Ihre Daten wurden erfolgreich aktualisiert",
                "email": "E-Mail",
                "emailConfirm": "E-Mail bestätigen",
                "firstName": "Vornamen",
                "inputRuleEmail": "Bitte geben Sie Ihre E-Mail Adresse ein",
                "inputRuleEmailConfirm": "Bitte bestätigen Sie Ihre E-Mail Adresse",
                "inputRuleFirstName": "Bitte geben Sie Ihren Vornamen ein",
                "inputRuleLastName": "Bitte geben Sie Ihren Nachnamen ein",
                "inputRulePassword": "Bitte geben Sie ein gültiges Passwort ein",
                "inputRulePasswordConfirm": "Bitte bestätigen Sie das Passwort",
                "inputRulePasswordLength": "Bitte geben Sie zwischen 6 und 16 Zeichen ein",
                "inputRuleValidEmail": "Bitte geben Sie eine gültige E-Mail Adresse ein",
                "lastName": "Nachname",
                "password": "Passwort",
                "passwordConfirm": "Bestätigen Sie das Passwort",
                "title": "Benutzereinstellungen",
                "subTitle": "Ändern Sie Ihre persönlichen Daten, Ihre E-Mail und Ihr Passwort.",
                "shortDescription": "Kurze Beschreibung Ihrer Person",
                "linkedinProfileUrl": "Ihre LinkedIn-Profil-URL",
                "userPicture": {
                    "title": "Aktualisieren Sie Ihr Profilbild",
                    "action": "Ein neues Bild hochladen",
                    "updateSuccess": "Ihr Profilbild wurde erfolgreich aktualisiert"
                },
                "delete": {
                    "title": "Ihr Konto löschen",
                    "description": "Starten Sie den Prozess der endgültigen Löschung Ihres Kontos. Sie haben 5 Tage Zeit, es wiederherzustellen, bevor es endgültig gelöscht wird. Tun Sie dies nur, wenn Sie sicher sind, dass Sie Ihr Konto nicht mehr benötigen.",
                    "action": "Konto löschen",
                    "confirm": {
                        "title": "Bestätigung der Kontolöschung",
                        "message": "Sind Sie sicher, dass Sie Ihr Konto löschen wollen? Sie haben 5 Tage Zeit, es wiederherzustellen. Nach Ablauf dieser Frist werden Ihr Konto und alle seine Daten endgültig gelöscht."
                    },
                    "success": {
                        "title": "Konto wartet auf Löschung",
                        "message": "Ihr Konto wird gelöscht. Sie haben 5 Tage Zeit, es wiederherzustellen. Nach Ablauf dieser Frist werden Ihr Konto und alle seine Daten endgültig gelöscht."
                    },
                    "error": {
                        "title": "Fehler beim Löschen des Kontos",
                        "message": "Beim Versuch, Ihr Konto zu löschen, ist ein Fehler aufgetreten. Bitte versuchen Sie es später noch einmal."
                    }
                },
                "undelete": {
                    "title": "Gelöschtes Konto wiederherstellen",
                    "description": "Brechen Sie den Löschvorgang ab und stellen Sie Ihr Konto wieder her. Sie haben 5 Tage nach der Löschung Zeit, es wiederherzustellen.",
                    "closingTime": "Der Löschvorgang begann um {timeStart} und wird um {timeEnd} enden",
                    "action": "Konto wiederherstellen",
                    "success": {
                        "title": "Konto wiederhergestellt",
                        "message": "Ihr Konto wurde erfolgreich wiederhergestellt."
                    },
                    "error": {
                        "title": "Fehler beim Wiederherstellen des Kontos",
                        "message": "Beim Versuch, Ihr Konto wiederherzustellen, ist ein Fehler aufgetreten. Bitte versuchen Sie es später noch einmal."
                    }
                }
            }
        }
    },
    "nl": {
        "user": {
            "settings": {
                "changeEmail": "E-mail wijzigen",
                "changeEmailSuccess": "Je e-mail is succesvol bijgewerkt",
                "buttonChangeEmail": "Update e-mail",
                "buttonChangePassword": "Wachtwoord bijwerken",
                "changePassword": "Wijzig wachtwoord",
                "changePasswordDesc": "Zorg ervoor dat het tussen de 8 en 16 tekens bevat, een combinatie van hoofdletters en kleine letters, een combinatie van letters en cijfers en ten minste één speciaal teken.",
                "changePasswordFailure": "Er is een fout opgetreden tijdens het wijzigen van je wachtwoord. Probeer het later nog eens.",
                "changePasswordSuccess": "Uw wachtwoord is succesvol bijgewerkt",
                "dataUpdateFailure": "Er heeft zich een fout voorgedaan. Probeer het later nog eens.",
                "dataUpdateSuccess": "Uw gegevens zijn succesvol bijgewerkt",
                "email": "E-mail",
                "emailConfirm": "Bevestig e-mail",
                "firstName": "Voornaam",
                "inputRuleEmail": "Voer uw e-mailadres in",
                "inputRuleEmailConfirm": "Gelieve uw e-mailadres te bevestigen",
                "inputRuleFirstName": "Voer uw voornaam in",
                "inputRuleLastName": "Voer uw achternaam in",
                "inputRulePassword": "Voer een geldig wachtwoord in",
                "inputRulePasswordConfirm": "Bevestig het wachtwoord",
                "inputRulePasswordLength": "Gelieve tussen 6 en 16 tekens in te voeren",
                "inputRuleValidEmail": "Voer een geldig e-mail adres in",
                "lastName": "Achternaam",
                "password": "Wachtwoord",
                "passwordConfirm": "Bevestig wachtwoord",
                "title": "Gebruikers Instellingen",
                "subTitle": "Wijzig je persoonlijke gegevens, e-mailadres en wachtwoord.",
                "shortDescription": "Korte beschrijving van jezelf",
                "linkedinProfileUrl": "Je LinkedIn profiel url",
                "userPicture": {
                    "title": "Werk je profielfoto bij",
                    "action": "Een nieuwe afbeelding uploaden",
                    "updateSuccess": "Je profielfoto is succesvol bijgewerkt"
                },
                "delete": {
                    "title": "Je account verwijderen",
                    "description": "Start het proces om je account definitief te verwijderen. Je hebt 5 dagen de tijd om het te herstellen voordat het voorgoed wordt verwijderd. Doe dit alleen als je zeker weet dat je je account niet meer nodig hebt.",
                    "action": "Account verwijderen",
                    "confirm": {
                        "title": "Account verwijderen bevestigen",
                        "message": "Weet je zeker dat je je account wilt verwijderen? Je hebt 5 dagen de tijd om het te herstellen. Na deze termijn worden je account en alle bijbehorende gegevens permanent verwijderd."
                    },
                    "success": {
                        "title": "Account wacht op verwijdering",
                        "message": "Je account wordt verwijderd. Je hebt 5 dagen de tijd om het te herstellen. Na deze termijn worden je account en alle bijbehorende gegevens permanent verwijderd."
                    },
                    "error": {
                        "title": "Fout bij verwijderen account",
                        "message": "Er is een fout opgetreden tijdens het verwijderen van je account. Probeer het later nog eens."
                    }
                },
                "undelete": {
                    "title": "Verwijderde account herstellen",
                    "description": "Annuleer het verwijderingsproces en herstel je account. Je hebt 5 dagen sinds de verwijdering om het te herstellen.",
                    "closingTime": "Het verwijderingsproces is gestart op {timeStart} en zal eindigen rond {timeEnd}.",
                    "action": "Account herstellen",
                    "success": {
                        "title": "Account hersteld",
                        "message": "Je account is succesvol hersteld."
                    },
                    "error": {
                        "title": "Fout bij het herstellen van account",
                        "message": "Er is een fout opgetreden tijdens het herstellen van je account. Probeer het later nog eens."
                    }
                }
            }
        }
    }
}
</i18n>

<script>
import Section from '@/components/v3/section/Section'
import Card from '@/components/v3/cards/Card'
import Tooltip from '@/components/v3/tooltips/Tooltip'
import PrimaryButton from '@/components/v3/buttons/PrimaryButton.vue'
import UploadButton from '@/components/v3/buttons/UploadButton'
import ImageLoader from '@/components/v3/images/ImageLoader'
import Loading from '@/components/v3/loading/Loading.vue'
import SectionHeader from '@/components/v3/section/SectionHeader.vue'
import {userServices} from '@/modules/users/services/UserServices'
import {User} from '@/modules/users/models/User'

import { getModule } from '@/modules/store'
import ConfigStore from '@/modules/config/store/ConfigStore'
import ConfigModule from '@/modules/config/'
import { getAppInstance } from '@/modules/app'

import date from 'date-and-time'

import _ from 'lodash'

const strongPwdRegex = new RegExp("^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#\$%\^&\*])(?=.{8,})")

export default {
    components: {
        Section,
        Card,
        Tooltip,
        PrimaryButton,
        Loading,
        SectionHeader,
        UploadButton,
        ImageLoader
    },
    props: {
    },
    data() {
        return {
            user: {},
            isLoading: false,
            isLoadingEmail: false,
            isPictureLoading: false,
            isLoadingPassword: false,
            isLoadingDelete: false,
            formData: new User(),
            formEmailData: {
                email: '',
                emailConfirm: ''
            },
            formPasswordData: {
                password: '',
                passwordConfirm: ''
            },
            formRules: {
                firstName: {
                    required: true,
                    whitespace: true,
                    min: 2,
                    max: 128,
                    trigger: 'blur'
                },
                lastName: {
                    required: true,
                    whitespace: true,
                    min: 2,
                    max: 128,
                    trigger: 'blur'
                },
                shortDescription: {
                    required: false,
                    whitespace: true,
                    min: 0,
                    max: 256,
                    trigger: 'blur'
                },
            },
            formEmailRules: {
                email: {
                    required: true,
                    type: 'email',
                    trigger: 'blur'
                },
                emailConfirm: {
                    validator: this.checkEmailConfirm,
                    trigger: 'blur'
                }
            },
            formPasswordRules: {
                password: {
                    required: true,
                    validator: this.checkPassword,
                    trigger: 'blur'
                },
                passwordConfirm: {
                    validator: this.checkPasswordConfirm,
                    trigger: 'blur'
                }
            }
        }
    },
    mounted() {
        this.getUserData()
    },
    computed: {
        configStore() {
            return getModule(ConfigStore)
        },
        userPictureUrl() {
            return this.user.pictureUrl
        },
        canDelete() {
            return this.user.closingTimeStamp === null
        },
        canUnDelete() {
            return this.user.closingTimeStamp !== null
        },
        userId() {
            return this.user.userId
        },
        accountClosingTimeStart() {
            return this.user.closingTimeStampStr
        },
        accountClosingTimeEnd() {
            let value = this.user.closingTimeStampStr
            console.log('accountClosingTimeEnd value', value)
            value = date.parse(value, 'YYYY/MM/DD HH:mm:ss [GMT]Z')
            console.log('accountClosingTimeEnd', value, date.parse(this.accountClosingTimeStart, 'YYYY/MM/DD HH:mm:ss [GMT]Z'))
            if (isNaN(value) || !value) return ''
            value = date.addDays(value, 5)
            return date.format(value, 'YYYY/MM/DD HH:mm:ss [GMT]Z')
        }
    },
    methods: {
        // checkCommonRule(rule, value, callback, source, options) {
        //     let error = ''

        //     if (rule.required === true && _.isEmpty(value)) {
        //         error = options.messages.required
        //     } else {
        //         if (rule.type === 'string') {
        //             if (rule.min )
        //         }
        //     }
        //     console.log('checkCommonRule', rule, value, callback, source, options)
        //     callback()
        //     return
        //     if (this.formEmailData.emailConfirm !== this.formEmailData.email) {
        //         callback(new Error(this.$t('user.settings.inputRuleEmailConfirm')))
        //     } else {
        //         callback()
        //     }
        // },
        checkEmailConfirm(rule, value, callback, source, options) {
            // console.log('checkEmailConfirm', this.formEmailData, rule, value, callback, source, options)
            if (this.formEmailData.emailConfirm !== this.formEmailData.email) {
                callback(new Error(this.$t('user.settings.inputRuleEmailConfirm')))
            } else {
                callback()
            }
        },
        checkPassword(rule, value, callback) {
            if (strongPwdRegex.test(value)) {
                callback()
            } else {
                callback(new Error(this.$t('user.settings.inputRulePassword')))
            }
        },
        checkPasswordConfirm(rule, value, callback) {
            console.log(rule)
            if (value !== this.formPasswordData.password) {
                callback(new Error(this.$t('user.settings.inputRulePasswordConfirm')))
            } else {
                callback()
            }
        },
        getUserData() {
            this.isLoading = true
            const configModule = getAppInstance().getModuleByType(ConfigModule)
            configModule.getUserData().then(response => {
                this.user = response
                this.resetFormData()
            }).catch(error => {
                this.$showNotification(
                    error.errors,
                    'error'
                )
            }).finally(() => {
                this.isLoading = false
            })
        },
        resetFormData() {
            console.log('resetFormData')
            this.formData = Object.assign(new User(), this.user)
            this.formEmailData.email = this.formData.email
        },
        reset() {
            console.log('reset')
            this.resetFormData()
        },
        promptDeleteUser() {
            this.$confirm(this.$t('user.settings.delete.confirm.message'), this.$t('user.settings.delete.confirm.title'), {
                confirmButtonText: this.$t('commons.delete'),
                cancelButtonText: this.$t('commons.cancel')
            }).then(() => {
                this.deleteUser()
            })
        },
        deleteUser() {
            this.isLoadingDelete++
            userServices.deleteUser(this.userId).then(data => {
                this.$showNotification(
                    this.$t('user.settings.delete.success.message'),
                    'success'
                )

            }).catch(error => {
                this.$showNotification(
                    error.errors,
                    'error'
                )
            }).finally(() => {
                this.isLoadingDelete--
                this.getUserData()
            })
        },
        undeleteUser() {
            this.isLoadingDelete++
            userServices.undeleteUser(this.userId).then(data => {
                this.$showNotification(
                    this.$t('user.settings.undelete.success.message'),
                    'success'
                )

            }).catch(error => {
                this.$showNotification(
                    error.errors,
                    'error'
                )
            }).finally(() => {
                this.isLoadingDelete--
                this.getUserData()
            })
        },
        save() {
            this.isLoading = true
            let isValidated = false

            this.$refs.form.validate(function (success, res) {
                isValidated = success
                console.log('save validation', success, res)
            })

            if (!isValidated) {
                this.$showNotification(this.$t('commons.inputRuleError'), 'error')
                this.isLoading = false
                return
            }

            userServices.updateUser(this.formData).then(response => {
                this.getUserData()
                this.$showNotification(
                    this.$t('user.settings.dataUpdateSuccess'),
                    'success'
                )
                this.$refs.form.clearValidate()
            }).catch(error => {
                this.$showNotification(
                    error.errors,
                    'error'
                )
            }).finally(() => {
                this.isLoading = false
            })
        },
        changeUserEmail() {
            this.isLoadingEmail = true
            let isValidated = false

            this.$refs.formEmail.validate(function (success, res) {
                isValidated = success
            })

            if (!isValidated) {
                this.$showNotification(
                    this.$t('commons.inputRuleError'),
                    'error'
                )
                this.isLoadingEmail = false
                return
            }

            let oldEmail = this.formData.email
            let newEmail = this.formEmailData.email
            userServices.changeUserEmail(oldEmail, newEmail).then(response => {
                this.$showNotification(
                    this.$t('user.settings.changeEmailSuccess'),
                    'success'
                )
                this.getUserData()
            }).catch(error => {
                this.$showNotification(
                    error.errors,
                    'error'
                )
            }).finally(() => {
                this.isLoadingEmail = false
            })
        },
        changeUserPassword() {
            this.isLoadingPassword = true
            let isValidated = false

            this.$refs.formPassword.validate(function (success, res) {
                isValidated = success
            })

            if (!isValidated) {
                this.$showNotification(
                    this.$t('commons.inputRuleError'),
                    'error'
                )
                this.isLoadingPassword = false
                return
            }

            let newPassword = this.formPasswordData.password
            userServices.changeUserPassword(newPassword).then(response => {
                this.$showNotification(
                    this.$t('user.settings.changePasswordSuccess'),
                    'success'
                )
            }).catch(error => {
                this.$showNotification(
                    error.errors,
                    'error'
                )
            }).finally(() => {
                this.isLoadingPassword = false
            })
        },
        onUserPictureDataUploaded (data, fileName) {
            console.log('onUserPictureDataUploaded', data, fileName)
            this.isPictureLoading = true
            userServices.updateUserPicture(this.userId, data).then(response => {
                console.log('user picture update SUCCESS', response)
                this.$showNotification(
                    this.$t('user.settings.userPicture.updateSuccess'),
                    'success'
                )
                this.getUserData()
            }).catch(error => {
                console.log('user picture update ERROR', error)
                this.$showNotification(
                    error.errors,
                    'error'
                )
                this.getUserData()
            }).finally(() => {
                this.isPictureLoading = false
            })
        }
    }
}
</script>

<style lang="stylus">
@import "~assets/v3/css/variables.styl";

$radio-size = 24px

.user-settings

    .card

        h2
            margin-bottom 20px !important

        form

            h2
                margin-top 0

        footer
            margin-top 0

        &:not(:first-child)
            margin-top 32px

        .el-checkbox.is-bordered
            margin 8px 16px 8px 0 !important

            &+.el-checkbox.is-bordered
                margin 0

    p
        color $color-blue-6

    .user-settings-block
        margin-bottom 20px

        p
            margin-bottom 10px

    .el-switch__label
        color $cv-color-grey-1

        &.is-active
            color $cv-color-blue-3

    .button-container-left
        text-align left
        margin-bottom 16px

    .button-container
        text-align right
        margin-top 16px

    .linkedin-input

        .el-input-group__prepend
            padding-left 40px

            img
                position absolute
                height 20px
                left 7px
                top 7px

    .image-loader
        margin-bottom 22px
        display flex
        justify-content center
        min-width 50px
        min-height 50px

        img
            max-width 256px

</style>



// WEBPACK FOOTER //
// src/modules/users/components/UserSettings.vue